import {
    useFieldArray,
    useWatch,
    Control,
    UseFormRegister,
    UseFormSetValue,
    UseFormGetValues,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, FormItem } from 'vnetwork-ui'
import { useCallback, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableHeader,
    TableBody,
    TableFooter,
    TableHead,
    TableRow,
    TableCell,
} from '@/components/ui/table'
import dayjs from 'dayjs'
import 'dayjs/locale/vi'

export interface MonthlyTarget {
    month: number
    presentation: number
    poc: number
    contract: number
    revenue: number
}

export interface ActivityTargetFormData {
    months: MonthlyTarget[]
    quickFill: {
        presentation: number
        poc: number
        contract: number
        revenue: number
    }
}

interface ActivityTargetFormProps {
    control: Control<any>
    register: UseFormRegister<any>
    setValue: UseFormSetValue<any>
    getValues: UseFormGetValues<any>
    isLoading?: boolean
}

// Get localized month name using dayjs
const getMonthName = (monthIndex: number): string => {
    return dayjs().locale('vi').month(monthIndex).format('MMMM')
}

export const createEmptyMonthsData = (): MonthlyTarget[] => {
    return Array.from({ length: 12 }, (_, i) => ({
        month: i + 1,
        presentation: 0,
        poc: 0,
        contract: 0,
        revenue: 0,
    }))
}

export default function ActivityTargetForm({
    control,
    register,
    setValue,
    getValues,
    isLoading,
}: ActivityTargetFormProps) {
    const { t } = useTranslation('report')

    const { fields } = useFieldArray({
        control,
        name: 'months',
    })

    // Use useWatch for reactive updates
    const watchedMonths = useWatch({ control, name: 'months' })

    const handleQuickFill = useCallback(() => {
        const quickFill = getValues('quickFill')
        const newMonths = (watchedMonths || []).map((m: MonthlyTarget) => ({
            ...m,
            presentation: quickFill.presentation || 0,
            poc: quickFill.poc || 0,
            contract: quickFill.contract || 0,
            revenue: quickFill.revenue || 0,
        }))
        setValue('months', newMonths)
    }, [getValues, watchedMonths, setValue])

    const handleClearAll = useCallback(() => {
        setValue('months', createEmptyMonthsData())
    }, [setValue])

    // Calculate total targets
    const totals = useMemo(() => {
        if (!watchedMonths)
            return { presentation: 0, poc: 0, contract: 0, revenue: 0 }
        return watchedMonths.reduce(
            (
                acc: {
                    presentation: number
                    poc: number
                    contract: number
                    revenue: number
                },
                m: MonthlyTarget
            ) => ({
                presentation: acc.presentation + (m.presentation || 0),
                poc: acc.poc + (m.poc || 0),
                contract: acc.contract + (m.contract || 0),
                revenue: acc.revenue + (m.revenue || 0),
            }),
            { presentation: 0, poc: 0, contract: 0, revenue: 0 }
        )
    }, [watchedMonths])

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-8 text-muted-foreground">
                {t('target_analytics.loading-existing-targets')}
            </div>
        )
    }

    return (
        <>
            {/* Quick Fill Section */}
            <div className="border rounded-lg p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold">
                        {t('target_analytics.quick-fill-all-months')}
                    </h4>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            size="small"
                            variant="contained"
                            onClick={handleQuickFill}
                        >
                            {t('target_analytics.apply-to-all')}
                        </Button>
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={handleClearAll}
                        >
                            {t('target_analytics.clear-all')}
                        </Button>
                    </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                    <FormItem
                        control={control}
                        name="quickFill.presentation"
                        type="number"
                        label={t('presentation')}
                    />
                    <FormItem
                        control={control}
                        name="quickFill.poc"
                        type="number"
                        label={t('poc')}
                    />
                    <FormItem
                        control={control}
                        name="quickFill.contract"
                        type="number"
                        label={t('contract')}
                    />
                    <FormItem
                        control={control}
                        name="quickFill.revenue"
                        type="number"
                        label={t('target_analytics.target-revenue')}
                    />
                </div>
            </div>

            {/* Month Grid */}
            <div className="border rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                            <TableHead className="w-36 font-semibold">
                                {t('month')}
                            </TableHead>
                            <TableHead className="text-center font-semibold">
                                {t('presentation')}
                            </TableHead>
                            <TableHead className="text-center font-semibold">
                                {t('poc')}
                            </TableHead>
                            <TableHead className="text-center font-semibold">
                                {t('contract')}
                            </TableHead>
                            <TableHead className="text-center font-semibold">
                                {t('target_analytics.target-revenue')}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {fields.map((field, index) => (
                            <TableRow key={field.id}>
                                <TableCell className="font-medium capitalize">
                                    {getMonthName(index)}
                                </TableCell>
                                <TableCell>
                                    <Input
                                        {...register(
                                            `months.${index}.presentation`,
                                            { valueAsNumber: true }
                                        )}
                                        type="number"
                                        min={0}
                                        className="w-full h-9 text-center"
                                        placeholder="0"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        {...register(`months.${index}.poc`, {
                                            valueAsNumber: true,
                                        })}
                                        type="number"
                                        min={0}
                                        className="w-full h-9 text-center"
                                        placeholder="0"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        {...register(
                                            `months.${index}.contract`,
                                            { valueAsNumber: true }
                                        )}
                                        type="number"
                                        min={0}
                                        className="w-full h-9 text-center"
                                        placeholder="0"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        {...register(
                                            `months.${index}.revenue`,
                                            { valueAsNumber: true }
                                        )}
                                        type="number"
                                        min={0}
                                        className="w-full h-9 text-center"
                                        placeholder="0"
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell className="font-bold">
                                {t('total')}
                            </TableCell>
                            <TableCell className="text-center font-bold text-primary">
                                {totals.presentation}
                            </TableCell>
                            <TableCell className="text-center font-bold text-primary">
                                {totals.poc}
                            </TableCell>
                            <TableCell className="text-center font-bold text-primary">
                                {totals.contract}
                            </TableCell>
                            <TableCell className="text-center font-bold text-primary">
                                {totals.revenue.toLocaleString()}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </div>
        </>
    )
}
